import { utils } from '@hh/global.js'

const { debounce } = utils

class HHFiltersWrapper extends HTMLElement {
  constructor() {
    super()
    this.filterData = []
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this)
    this.closeDetails = this.closeDetails.bind(this)
    this.onHistoryChange = this.onHistoryChange.bind(this)
    this.closeMobileFilters = this.closeMobileFilters.bind(this)
    this.debouncedOnSubmit = debounce((event) => {
      this.onSubmitHandler(event)
    }, 500).bind(this)
  }

  connectedCallback() {
    const submitInputs = this.querySelectorAll('.js-filter-submit')
    const clearButton = this.querySelector('.js-filter-clear')
    const sortInput = this.querySelector('.js-sort-input')
    const shadowSortInput = this.querySelector('.js-shadow-sort-input')
    const booleanToggles = this.querySelectorAll('.js-filter-boolean')
    const closeButton = this.querySelector('.js-close')
    const form = this.querySelector('form')

    this.bindActiveFacetButtonEvents()
    window.addEventListener('popstate', this.onHistoryChange)
    // Prevent accordions from closing.
    // document.body.addEventListener('click', this.closeDetails)
    // Updating on form change instead of submit
    // submitInputs.forEach((element) => {
    //   element.addEventListener('click', this.debouncedOnSubmit)
    // })

    form?.addEventListener('change', this.debouncedOnSubmit)
    clearButton?.addEventListener('click', this.clearFilters.bind(this))
    sortInput && sortInput.addEventListener('change', this.debouncedOnSubmit)
    shadowSortInput &&
      shadowSortInput.addEventListener(
        'change',
        this.onShadowSortInputChange.bind(this)
      )
    closeButton?.addEventListener('click', this.closeMobileFilters)
    booleanToggles.forEach((toggle) => {
      toggle.addEventListener('click', this.passBooleanClick.bind(this))
    })
  }

  closeMobileFilters() {
    this.dispatchEvent(
      new CustomEvent('closeModal', {
        bubbles: true,
      })
    )
  }

  closeDetails(event) {
    const detailsElements = this.querySelectorAll('.js-filter')
    detailsElements.forEach((detailsElement) => {
      if (!detailsElement.contains(event.target)) {
        detailsElement.open = false
      }
    })
  }

  onSubmitHandler(event) {
    event.preventDefault()
    const formData = new FormData(event.target.closest('form'))
    const searchParams = new URLSearchParams(formData)

    // Check if the toddler filter is set to 0
    // This is needed since Shopify filters don't really support toggle functionality by default.
    // Might want to put the param somewhere more global in the future.
    const toddlerParam = 'filter.p.m.hhschleich.suitable_for_toddlers'
    const toddlerSearchParam = searchParams.get(toddlerParam)
    if (toddlerSearchParam === '0') {
      searchParams.delete(toddlerParam)
    }

    const searchParamsString = searchParams.toString()
    this.renderPage(searchParamsString, event)
  }

  onShadowSortInputChange(e) {
    const { value } = e.detail
    const mainSortInput = document.querySelector(
      `.js-main-sort input[value="${value}"]`
    )
    mainSortInput.click()
  }

  onActiveFilterClick(event) {
    event.preventDefault()
    this.toggleActiveFacets()
    this.renderPage(new URL(event.currentTarget.href).searchParams.toString())
  }

  onHistoryChange(event) {
    const searchParams = event.state?.searchParams || ''
    this.renderPage(searchParams, null, false)
  }

  toggleActiveFacets(disable = true) {
    document.querySelectorAll('.js-facet-remove').forEach((element) => {
      element.classList.toggle('disabled', disable)
    })
  }

  clearFilters(e) {
    e.preventDefault()
    const { target } = e
    const { clearUrl } = target.dataset

    const searchParams = clearUrl ?? ''

    this.renderPage(searchParams, e)
  }

  passBooleanClick(e) {
    const toggle = e.currentTarget.querySelector('hh-switch')
    if (e.target === toggle) return
    toggle.click()
  }

  renderPage(searchParams, event, updateURLHash = true) {
    document
      .getElementById('CollectionProductGrid')
      .querySelector('.js-product-grid')
      .classList.add('loading')

    const url =
      `${window.location.pathname}?` +
      this.getSections()
        .map((section) => `sections[]=${section.section}`)
        .join('&') +
      `&${searchParams}`

    const filterDataUrl = (element) => element.url === url

    this.filterData.some(filterDataUrl)
      ? this.renderSectionFromCache(filterDataUrl, event)
      : this.renderSectionFromFetch(url, event)

    if (updateURLHash) this.updateURLHash(searchParams)
  }

  renderSectionFromFetch(url, event) {
    fetch(url)
      .then((response) => response.json())
      .then((parsed) => {
        const html = Object.values(parsed).join('')
        this.filterData = [...this.filterData, { html, url }]
        this.renderFilters(html, event)
        this.renderProductGrid(html)
      })
  }

  renderSectionFromCache(filterDataUrl, event) {
    const html = this.filterData.find(filterDataUrl).html
    this.renderFilters(html, event)
    this.renderProductGrid(html)
  }

  renderProductGrid(html) {
    const innerHTML = new DOMParser()
      .parseFromString(html, 'text/html')
      .getElementById('CollectionProductGrid').innerHTML

    document.getElementById('CollectionProductGrid').innerHTML = innerHTML
  }

  renderFilters(...args) {
    this.renderSingleFormFilters('#CollectionFiltersForm', ...args)
    this.renderSingleFormFilters('#CollectionFiltersFormMobile', ...args)
  }

  renderSingleFormFilters(form, html, event) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html')

    const facetDetailsElements = parsedHTML.querySelectorAll(
      `${form} .js-filter`
    )

    const matchesIndex = (element) =>
      element.dataset.index ===
      event?.target.closest('.js-filter')?.dataset.index

    const facetsToRender = Array.from(facetDetailsElements).filter(
      (element) => !matchesIndex(element)
    )

    const countsToRender = Array.from(facetDetailsElements).find(matchesIndex)

    facetsToRender.forEach((element) => {
      document.querySelector(
        `${form} .js-filter[data-index="${element.dataset.index}"]`
      ).innerHTML = element.innerHTML
    })

    this.renderActiveFacets(parsedHTML)

    this.updateActiveCount(parsedHTML)
    this.updateTotalCount(parsedHTML)

    if (countsToRender) {
      this.renderCounts(countsToRender, event.target.closest('.js-filter'))
    }
  }

  renderActiveFacets(html) {
    const activeFacetElementSelectors = [
      '.js-facets--mobile',
      '.js-facets--desktop',
    ]

    activeFacetElementSelectors.forEach((selector) => {
      const activeFacetsElement = html.querySelector(selector)
      if (!activeFacetsElement) return
      document.querySelector(selector).innerHTML = activeFacetsElement.innerHTML
    })

    this.bindActiveFacetButtonEvents()
    this.toggleActiveFacets(false)
  }

  renderCounts(source, target) {
    const countElementSelectors = ['.js-bubble', '.js-selected']
    countElementSelectors.forEach((selector) => {
      const targetElement = target.querySelector(selector)
      const sourceElement = source.querySelector(selector)

      if (sourceElement && targetElement) {
        target.querySelector(selector).outerHTML =
          source.querySelector(selector).outerHTML
      }
    })
  }

  updateActiveCount(html) {
    const activeCountElement = html.querySelector('.js-active-count')
    const activeFacetCount = parseInt(activeCountElement?.textContent)

    const filterOpener = document.querySelector('.js-filter-drawer-opener')
    const filterClear = this.querySelector('.js-filter-clear')

    if (activeFacetCount > 0) {
      filterOpener?.classList.add(
        'collection-filters__filter-button--has-filters'
      )
      filterClear?.removeAttribute('disabled')
    } else {
      filterOpener?.classList.remove(
        'collection-filters__filter-button--has-filters'
      )
      filterClear?.setAttribute('disabled', true)
    }
  }

  updateTotalCount(html) {
    const totalCountDataAttr = html.querySelector('[data-total-items]')
    const totalCountElement = document.querySelector('.js-total-items-count')

    if (!totalCountDataAttr || !totalCountElement) return

    totalCountElement.textContent =
      totalCountDataAttr?.dataset?.totalItems ?? ''
  }

  bindActiveFacetButtonEvents() {
    document.querySelectorAll('.js-facet-remove').forEach((element) => {
      element.addEventListener('click', this.onActiveFilterClick, {
        once: true,
      })
    })
  }

  updateURLHash(searchParams) {
    history.pushState(
      { searchParams },
      '',
      `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`
    )
  }

  getSections() {
    return [
      {
        id: 'main-collection-filters',
        section: document.getElementById('main-collection-filters').dataset.id,
      },
      {
        id: 'main-collection-product-grid',
        section: document.getElementById('main-collection-product-grid').dataset
          .id,
      },
    ]
  }
}

customElements.define('hh-filters-wrapper', HHFiltersWrapper)
